import React, { useContext, useState, useEffect } from "react";
import { Button, Typography, CircularProgress, MenuItem } from "@mui/material";
import StyledTextField from "../../../common/StyledComponents/StyledTextField";
import AlertModal from "../../../common/alertModal/AlertModal";
import useAxios from "../../../../hooks/useAxios";
import "../index.css";
import { AuthContext } from "../../../../context/AuthContext";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

const TeacherForm = ({ handleSubmit, is_school_required=false }) => {
  const [formData, setFormData] = useState({
    full_name: "",
    number_of_students: "",
    number_of_classes: "",
    unique_identifier: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  // const [schools, setSchools] = useState([]);
  const { setStatus, status } = useContext(AuthContext);
  const axiosInstance = useAxios();
  const [schoolVerification, setSchoolVerification] = useState({
    verified: null,
    message: "",
    schoolName: "",
  });

  const [alert, setAlert] = useState({
    open: false,
    title: "",
    message: "",
    type: "success", // 'success' or 'error'
  });

  // // Fetch all schools on mount
  // useEffect(() => {
  //   const fetchSchools = async () => {
  //     try {
  //       const response = await axiosInstance.get("/users/schools");
  //       if (response.data.success) {
  //         setSchools(response.data.schools || []);
  //       }
  //     } catch (error) {
  //       console.error("Failed to fetch schools:", error);
  //     }
  //   };

  //   fetchSchools();
  // }, []);

  const validate = () => {
    const newErrors = {};
    if (!formData.full_name.trim()) newErrors.full_name = "Full Name is required.";
    if (formData.number_of_students && isNaN(formData.number_of_students)) {
      newErrors.number_of_students = "Number of Students must be a valid number.";
    } else if (formData.number_of_students && formData.number_of_students > 360) {
      newErrors.number_of_students = "Number of Students cannot exceed 360.";
    }
    if (formData.number_of_classes && isNaN(formData.number_of_classes)) {
      newErrors.number_of_classes = "Number of Classes must be a valid number.";
    }else if (formData.number_of_classes && formData.number_of_classes > 12) {
      newErrors.number_of_classes = "Number of classes cannot exceed 12.";
    }
    if (formData.unique_identifier && !schoolVerification.verified) {
      newErrors.unique_identifier = "Please verify the school identifier.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "unique_identifier") {
      setSchoolVerification({ verified: null, message: "", schoolName: "" });
    }
  };

  const verifySchool = async () => {
    if (!formData.unique_identifier.trim()) return;

    try {
      const response = await axiosInstance.post("profile/school-verification", {
        unique_identifier: formData.unique_identifier.trim(),
      });

      if (response.data.success) {
        setSchoolVerification({
          verified: true,
          message: "School identified successfully",
          schoolName: response.data.school_name,
        });
      } else {
        setSchoolVerification({
          verified: false,
          message: "Wrong school identifier",
          schoolName: "",
        });
      }
    } catch (error) {
      setSchoolVerification({
        verified: false,
        message: "An error occurred while verifying the school",
        schoolName: "",
      });
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setLoading(true);
      try {
        const result = await handleSubmit(formData);
        if (result.success) {
          const userStatus = result.status;
          if (formData.unique_identifier) {
            // Update status and redirect to dashboard if a school is selected
            if (userStatus) {
              setStatus(userStatus);
            }
            window.location.href = "/dashboard";
          } else {
            // Redirect to payment placeholder for standalone accounts
            window.location.href = "/dashboard";
          }
        } else {
          setAlert({
            open: true,
            title: "Error",
            message: result.message || "Unexpected error occurred.",
            type: "error",
          });
        }
      } catch (error) {
        setAlert({
          open: true,
          title: "Error",
          message: error.response?.data?.message || "An unexpected error occurred. Please try again.",
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <form onSubmit={onSubmit} className="profile-form">
        <Typography className="profile-form-title">
          Complete your <strong>teacher</strong> profile.
        </Typography>
        {status !== 'active' && (<StyledTextField
          required={is_school_required}
          label={is_school_required? "School Identifier" : "School Identifier (Optional)"}
          name="unique_identifier"
          value={formData.unique_identifier}
          onChange={handleChange}
          onBlur={verifySchool}
          type="text"
          fullWidth
          margin="normal"
          error={!!errors.unique_identifier}
          helperText={
            schoolVerification.verified === null
              ? "Provide the unique identifier for your school, if applicable."
              : schoolVerification.verified
              ? (
                  <>
                    <CheckCircleIcon style={{ color: "green", verticalAlign: "middle" }} />
                    {" "} <span className="warning-success">{"school name: "}</span> 
                    <span className="warning-success">{schoolVerification.schoolName}</span> 
                  </>
                )
              : (
                  <>
                    <ErrorIcon style={{ color: "red", verticalAlign: "middle" }} />
                    {" "}
                    <span className="warning-faild">{schoolVerification.message}</span> 
                  </>
                )
          }
        />)}
        <StyledTextField
          label="Full Name"
          name="full_name"
          value={formData.full_name}
          onChange={handleChange}
          fullWidth
          margin="normal"
          error={!!errors.full_name}
          helperText={errors.full_name}
          required
        />
        <StyledTextField
          label="Number of Students"
          name="number_of_students"
          value={formData.number_of_students}
          onChange={handleChange}
          type="number"
          fullWidth
          margin="normal"
          error={!!errors.number_of_students}
          helperText={errors.number_of_students}
          required
        />
        <StyledTextField
          label="Number of Classes"
          name="number_of_classes"
          value={formData.number_of_classes}
          onChange={handleChange}
          type="number"
          fullWidth
          margin="normal"
          error={!!errors.number_of_classes}
          helperText={errors.number_of_classes}
          required
        />
        {/* <StyledTextField
          select
          label="School (Optional)"
          name="school_id"
          value={formData.school_id}
          onChange={handleChange}
          fullWidth
          margin="normal"
        >
          <MenuItem value="">Standalone Account</MenuItem>
          {schools.map((school) => (
            <MenuItem key={school.id} value={school.id}>
              {school.school_profile?.school_name || "Unnamed School"}
            </MenuItem>
          ))}
        </StyledTextField> */}
        {/* <Typography className="note">
        - If you are part of a registered school, the account will be free of charge.
        </Typography>
        <Typography className="note">
        - If you are creating a standalone account, this will be a paid service.
        </Typography>
        <Typography className="note">
        - Please provide accurate numbers for students and classes. These will determine the price for standalone accounts and will be deducted of the school account if not.
        </Typography> */}
        <Typography className="note">Fields marked with (*) are required.</Typography>
        <Button
          type="submit"
          className="primary-button"
          fullWidth
          sx={{ marginTop: "20px", padding: "10px" }}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Submit"}
        </Button>
      </form>

      <AlertModal
        open={alert.open}
        onClose={() => {
          setAlert((prev) => ({ ...prev, open: false }));
        }}
        title={alert.title}
        message={alert.message}
        type={alert.type}
      />
    </>
  );
};

export default TeacherForm;
